/**
 * The one screen an unauthenticated visitor can reach.
 *
 * It is three forms wearing one shell, because which one applies is a
 * property of the instance rather than a choice the visitor makes:
 *
 *   - no accounts exist yet  -> create the first administrator
 *   - a password was reset   -> set your own before continuing
 *   - otherwise              -> sign in, and register where that is allowed
 *
 * The error text never distinguishes "no such user" from "wrong password" --
 * that is enforced on the server, and repeating the distinction here would
 * undo it.
 */
import { useState, type FormEvent } from 'react'
import { ApiError, api } from '~/lib/api.ts'
import { useAuth } from '~/lib/auth.tsx'
import { useDocumentTitle } from '~/lib/title.ts'
import { useT } from '~/i18n/index.tsx'
import { Button } from '~/components/ui/primitives.tsx'
import { cn } from '~/lib/cn.ts'

type Mode = 'signin' | 'register'

export function LoginScreen() {
  const { t } = useT()
  const { setupRequired, registration, refresh, user } = useAuth()
  const [mode, setMode] = useState<Mode>('signin')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Computed before the early return below, so the hook it feeds runs on
  // every render regardless of which branch this screen takes.
  const registering = mode === 'register' && !setupRequired
  const title = setupRequired ? t('auth.setupTitle')
    : registering ? t('auth.registerTitle')
    : t('auth.signIn')
  useDocumentTitle(user?.mustChangePassword ? t('auth.changeTitle') : title)

  // A pending password change outranks everything: the session exists but
  // the server refuses every other route until it is dealt with.
  if (user?.mustChangePassword) return <ChangePassword />

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null); setNotice(null); setBusy(true)
    const f = new FormData(e.currentTarget)
    const body: Record<string, string> = {
      username: String(f.get('username') ?? '').trim().toLowerCase(),
      password: String(f.get('password') ?? ''),
    }
    const display = String(f.get('displayName') ?? '').trim()
    if (display) body.displayName = display
    const invite = String(f.get('invite') ?? '').trim()
    if (invite) body.invite = invite

    const path = setupRequired ? '/api/auth/setup'
      : registering ? '/api/auth/register'
      : '/api/auth/login'

    try {
      const res = await api<{ pending?: boolean }>(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.pending) {
        setNotice(t('auth.registerPending'))
        setMode('signin')
      }
      await refresh()
    } catch (err) {
      setError((err as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell title={title}>
      {setupRequired && (
        <p className="mb-3 max-w-[46ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
          {t('auth.setupWhy')}
        </p>
      )}

      <form onSubmit={submit} className="space-y-3">
        <TextInput name="username" label={t('auth.username')} autoComplete="username" autoFocus required />
        <TextInput
          name="password" type="password" label={t('auth.password')}
          autoComplete={setupRequired || registering ? 'new-password' : 'current-password'}
          hint={setupRequired || registering ? t('auth.minLength', { n: '12' }) : undefined}
          required
        />
        {(setupRequired || registering) && (
          <TextInput name="displayName" label={t('auth.displayName')} hint={t('auth.displayNameHint')} />
        )}
        {registering && registration === 'invite' && (
          <TextInput name="invite" label={t('auth.inviteCode')} required />
        )}

        {error && <Message tone="fail">{error}</Message>}
        {notice && <Message tone="pass">{notice}</Message>}

        <Button type="submit" variant="primary" disabled={busy} className="w-full">
          {busy ? t('auth.signingIn')
            : setupRequired ? t('auth.setupSubmit')
            : registering ? t('auth.registerSubmit')
            : t('auth.submit')}
        </Button>
      </form>

      {!setupRequired && registration !== 'closed' && (
        <button
          type="button"
          onClick={() => { setMode(registering ? 'signin' : 'register'); setError(null) }}
          className="link-inline mt-3 text-[length:var(--text-data)] text-[var(--accent)]"
        >
          {registering ? t('auth.haveAccount') : t('auth.needAccount')}
        </button>
      )}
    </Shell>
  )
}

/** Forced on a user whose password an admin reset. */
function ChangePassword() {
  const { t } = useT()
  const { refresh, logout } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    const f = new FormData(e.currentTarget)
    const next = String(f.get('newPassword') ?? '')
    if (next !== String(f.get('confirmPassword') ?? '')) {
      setError(t('auth.mismatch')); return
    }
    setBusy(true)
    try {
      await api('/api/auth/password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ currentPassword: String(f.get('currentPassword') ?? ''), newPassword: next }),
      })
      // The server ends every session on a password change, so there is
      // nothing to refresh into -- signing back in is the honest next step.
      setDone(true)
      await logout()
    } catch (err) {
      setError((err as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return <Shell title={t('auth.signIn')}><Message tone="pass">{t('auth.changedSignIn')}</Message></Shell>

  return (
    <Shell title={t('auth.changeTitle')}>
      <p className="mb-3 max-w-[46ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
        {t('auth.changeWhy')}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <TextInput name="currentPassword" type="password" label={t('auth.currentPassword')} autoComplete="current-password" autoFocus required />
        <TextInput name="newPassword" type="password" label={t('auth.newPassword')} autoComplete="new-password" hint={t('auth.minLength', { n: '12' })} required />
        <TextInput name="confirmPassword" type="password" label={t('auth.confirmPassword')} autoComplete="new-password" required />
        {error && <Message tone="fail">{error}</Message>}
        <Button type="submit" variant="primary" disabled={busy} className="w-full">
          {t('auth.changeSubmit')}
        </Button>
      </form>
      <button type="button" onClick={() => void logout()}
        className="link-inline mt-3 text-[length:var(--text-micro)] text-[var(--text-muted)]">
        {t('auth.signOut')}
      </button>
    </Shell>
  )
}

// ---------------------------------------------------------------------

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-[24rem]">
        <div className="rounded-[var(--radius-container)] border border-[var(--border)] bg-[var(--bg-raised)] px-4 py-4">
          <h1 className="mb-3 text-[length:var(--text-title)] font-medium text-[var(--text)]">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  )
}

export function TextInput({
  name, label, hint, type = 'text', ...rest
}: {
  name: string; label: string; hint?: string; type?: string
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="block text-[length:var(--text-micro)] font-medium uppercase tracking-[0.04em] text-[var(--text-faint)]">
        {label}
      </span>
      <input
        name={name}
        type={type}
        className={cn(
          'mt-1 block w-full rounded-[var(--radius-control)] border border-[var(--border)]',
          'bg-[var(--bg)] px-2.5 text-[length:var(--text-data)] text-[var(--text)]',
          'h-[var(--control-h-md)]',
          'focus:border-[var(--accent)] focus:outline-none',
        )}
        {...rest}
      />
      {hint && (
        <span className="mt-0.5 block text-[length:var(--text-micro)] text-[var(--text-faint)]">{hint}</span>
      )}
    </label>
  )
}

export function Message({ tone, children }: { tone: 'fail' | 'pass'; children: React.ReactNode }) {
  return (
    <p
      className={cn(
        'rounded-[var(--radius-control)] border px-2.5 py-1.5 text-[length:var(--text-data)]',
        tone === 'fail'
          ? 'border-[var(--fail)]/30 bg-[var(--fail-bg)] text-[var(--fail)]'
          : 'border-[var(--pass)]/30 bg-[var(--pass-bg)] text-[var(--pass)]',
      )}
    >
      {children}
    </p>
  )
}
